import React, { FC, useMemo } from 'react'
import { isEmpty, isNull } from 'lodash'
import { useReactiveVar } from '@apollo/client'
import { withStyles, Button, StepIconProps, MobileStepper, Theme } from '@material-ui/core'
import { StepLabel, Box, Typography, Grid } from '@mui/material'
import { useTheme, useMediaQuery } from '@material-ui/core'
import { useTranslation } from 'react-i18next'

import { stepperTypeVar, stepperHeaderTextVar, stepperCurrentStepVar } from '../../graphql/local'
import { Steppers } from '../../constants'
import {
  ColorLibConnector,
  CompletedIcon,
  Root,
  StyledStep,
  StyledStepper,
} from './StyledComponents'

const StyledMobileStepper = withStyles((theme: Theme) => ({
  root: {
    zIndex: 1,
    maxWidth: '100%',
    flexGrow: 1,
    backgroundColor: 'white',
    display: 'none',
    marginTop: -1,
    padding: theme.spacing(0, 0, 0, 0),
    [theme.breakpoints.up('sm')]: {
      display: 'block',
    },
    '& .MuiLinearProgress-barColorPrimary': {
      backgroundColor: '#D9D9D9',
    },
    '& .MuiLinearProgress-colorPrimary': {
      backgroundColor: '#D9D9D9',
      height: '1px',
    },
  },
  progress: {
    width: '100%',
    height: 2,
  },
}))(MobileStepper)

export function ColorlibStepIcon(props: StepIconProps) {
  const { active, completed, icon } = props

  return <Root ownerState={{ active }}>{completed ? <CompletedIcon /> : <div>{icon}</div>}</Root>
}

export const StepperLayout: FC = ({ children }) => {
  const theme = useTheme()
  const matchesBreakpoints: boolean = useMediaQuery(theme.breakpoints.up('sm'))
  const { t } = useTranslation()

  const stepperType = useReactiveVar(stepperTypeVar)
  const currentStep = useReactiveVar(stepperCurrentStepVar)
  const headerText = useReactiveVar(stepperHeaderTextVar)

  const steps = useMemo(() => {
    const stepper = !isNull(stepperType) && Steppers[stepperType]
    return stepper
      ? Object.entries(stepper).reduce((acc, [key, defaultText], idx) => {
          acc[key] = [idx, t(key, defaultText)]
          return acc
        }, {} as Record<string, [idx: number, label: string]>)
      : {}
  }, [stepperType])

  const renderMobileStepLabel = useMemo(
    () =>
      steps &&
      currentStep &&
      !matchesBreakpoints && (
        <Typography
          fontSize={theme.typography.body1.fontSize}
          fontWeight="bold"
          fontFamily={theme.typography.fontFamily}
          textAlign="center"
          marginBottom="1em"
        >
          {steps[currentStep][1]}
        </Typography>
      ),
    [matchesBreakpoints, steps, currentStep],
  )

  return (
    <>
      {isEmpty(steps) || !currentStep ? undefined : (
        <Box
          sx={{
            mb: { xs: 0, sm: 3 },
            overflowY: { xs: 'auto', sm: 'visible' },
          }}
        >
          <Grid size={12}>
            <Typography variant={'h1'}>{headerText}</Typography>
          </Grid>
          <StyledStepper connector={<ColorLibConnector />} activeStep={steps[currentStep][0]}>
            {Object.values(steps).map(([idx, label], index) => {
              return (
                <StyledStep
                  key={label}
                  sx={{
                    ...(index === steps[currentStep][0] && {
                      '&:not(.MuiStep-completed)': {
                        borderBottomColor: '#EF2828',
                      },
                    }),
                  }}
                  data-test={Object.keys(steps)[idx]}
                >
                  <StepLabel slots={{ stepIcon: ColorlibStepIcon }}>{label}</StepLabel>
                </StyledStep>
              )
            })}
          </StyledStepper>
          {renderMobileStepLabel}
          <StyledMobileStepper
            variant="progress"
            steps={3}
            position="static"
            activeStep={steps[currentStep][0]}
            nextButton={<Button style={{ display: 'none' }} />}
            backButton={<Button style={{ display: 'none' }} />}
          />
        </Box>
      )}
      {children}
    </>
  )
}
